import React from 'react'
import { History, RefreshCw } from 'lucide-react'
import type { FinishInfo, TransferHistoryItem } from '../../types/conversation.types'

export interface AttendanceHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  loading: boolean
  finishInfo: FinishInfo | null
  transferHistory: TransferHistoryItem[]
}

/**
 * Modal reutilizável: quem finalizou o atendimento + tabela de `conversation_transfers`.
 */
export const AttendanceHistoryModal: React.FC<AttendanceHistoryModalProps> = ({
  isOpen,
  onClose,
  loading,
  finishInfo,
  transferHistory
}) => {
  if (!isOpen) return null

  const hasTransfers = transferHistory.length > 0
  const hasFinish = Boolean(finishInfo)
  const empty = !hasFinish && !hasTransfers && !loading

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="attendance-history-modal-title"
      >
        <h3
          id="attendance-history-modal-title"
          className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"
        >
          <History className="w-5 h-5" aria-hidden />
          Histórico do atendimento
        </h3>
        {loading ? (
          <div className="flex justify-center py-6">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" aria-hidden />
          </div>
        ) : (
          <div className="space-y-4">
            {finishInfo && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Finalizado por</p>
                <p className="text-sm text-gray-600">
                  {finishInfo.user_name}{' '}
                  <span className="text-gray-400">(ID: {finishInfo.user_id})</span>
                </p>
              </div>
            )}
            {hasTransfers && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Histórico de transferências</p>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-600">De</th>
                        <th className="px-3 py-2 text-left text-gray-600">Para</th>
                        <th className="px-3 py-2 text-left text-gray-600">Data</th>
                        <th className="px-3 py-2 text-left text-gray-600">Mensagem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferHistory.map((t, i) => (
                        <tr key={t.id ?? i} className="border-t border-gray-100 align-top">
                          <td className="px-3 py-2">{t.from_name ?? '—'}</td>
                          <td className="px-3 py-2">{t.to_name ?? '—'}</td>
                          <td className="px-3 py-2 text-gray-500">
                            {t.created_at ? new Date(t.created_at).toLocaleString('pt-BR') : '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-600 whitespace-pre-wrap break-words max-w-xs">
                            {t.note && t.note.trim() ? t.note : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {empty && (
              <p className="text-sm text-gray-500">Nenhum dado de histórico disponível.</p>
            )}
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
